import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT, MlaRegistrationRequest } from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from './logging.service';

export interface RopcTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  id_token: string;
  scope?: string;
 oid: string;

}

export interface RopcErrorResponse {
  error: string;
  error_description: string;
  error_codes?: number[];
  timestamp?: string;
  trace_id?: string;
  correlation_id?: string;
}

export interface RopcUserInfo {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RopcAuthService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  async authenticate(username: string, password: string): Promise<RopcTokenResponse> {
    const oidcSettings = this.env.appSettings.oidcSettings;

    if (!oidcSettings?.ropcPolicy || !oidcSettings?.tenantId || !oidcSettings?.loginHost) {
      throw new Error('ROPC authentication is not configured. Missing ropcPolicy, tenantId, or loginHost.');
    }

    const tokenEndpoint = `https://${oidcSettings.loginHost}/${oidcSettings.tenantId}/${oidcSettings.ropcPolicy}/oauth2/v2.0/token`;

    const clientId = oidcSettings.apiClientId || oidcSettings.client_id;
    const body = new URLSearchParams();
    body.set('grant_type', 'password');
    body.set('client_id', clientId);
    body.set('username', username);
    body.set('password', password);
    body.set('scope', `openid profile ${clientId} offline_access`);
    body.set('response_type', 'token id_token');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    try {
      const response = await firstValueFrom(
        this.http.post<RopcTokenResponse>(tokenEndpoint, body.toString(), { headers })
      );
      return response;
    } catch (error: unknown) {
      const httpError = error as { error?: RopcErrorResponse };
      if (httpError.error?.error_description) {
        const errorDesc = httpError.error.error_description;

        if (
          errorDesc.includes('AADSTS50126') ||
          errorDesc.includes('AADB2C90225') ||
          errorDesc.includes('AADSTS50034') ||
          errorDesc.includes('Invalid username or password') ||
          errorDesc.includes('username or password provided in the request are invalid') ||
          errorDesc.includes('user account does not exist')
        ) {
          throw new Error("We can't seem to find your account");
        }
        if (errorDesc.includes('AADSTS50053') || errorDesc.includes('locked')) {
          throw new Error('Your account has been locked. Please try again later or reset your password.');
        }
        if (errorDesc.includes('AADSTS50057') || errorDesc.includes('disabled')) {
          throw new Error('Your account has been disabled. Please contact support.');
        }

        throw new Error(errorDesc);
      }

      throw new Error('Authentication failed. Please try again.');
    }
  }

  async register(registrationData: MlaRegistrationRequest): Promise<RopcTokenResponse> {
    const oidcSettings = this.env.appSettings.oidcSettings;

    if (!oidcSettings?.ropcSignupPolicy || !oidcSettings?.tenantId || !oidcSettings?.loginHost) {
      throw new Error('ROPC registration is not configured. Missing ropcSignupPolicy, tenantId, or loginHost.');
    }

    const tokenEndpoint = `https://${oidcSettings.loginHost}/${oidcSettings.tenantId}/${oidcSettings.ropcSignupPolicy}/oauth2/v2.0/token`;

    const clientId = oidcSettings.apiClientId || oidcSettings.client_id;

    const body = new URLSearchParams();
    body.set('grant_type', 'password');
    body.set('client_id', clientId);
    body.set('username', registrationData.email);
    body.set('password', '');
    body.set('scope', `openid profile ${clientId} offline_access`);
    body.set('response_type', 'token id_token');

    body.set('extension_FirstName', registrationData.firstName);
    body.set('extension_LastName', registrationData.lastName);
    if (registrationData.phone) {
      body.set('extension_Phone', registrationData.phone);
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    try {
      const response = await firstValueFrom(
        this.http.post<RopcTokenResponse>(tokenEndpoint, body.toString(), { headers })
      );
      return response;
    } catch (error: unknown) {
      const httpError = error as { error?: RopcErrorResponse };
      if (httpError.error?.error_description) {
        const errorDesc = httpError.error.error_description;

        if (errorDesc.includes('AADB2C99002') || errorDesc.includes('already exists')) {
          throw new Error('An account with this email already exists. Please sign in instead.');
        }
        if (errorDesc.includes('AADB2C90117') || errorDesc.includes('password')) {
          throw new Error('Password does not meet requirements. Please use at least 8 characters with uppercase, lowercase, and numbers.');
        }
        if (errorDesc.includes('AADB2C90118') || errorDesc.includes('email')) {
          throw new Error('Please enter a valid email address.');
        }

        throw new Error(errorDesc);
      }

      throw new Error('Registration failed. Please try again.');
    }
  }

  async registerWithPassword(
    registrationData: MlaRegistrationRequest,
    password: string
  ): Promise<RopcTokenResponse> {
    const oidcSettings = this.env.appSettings.oidcSettings;

    if (!oidcSettings?.ropcSignupPolicy || !oidcSettings?.tenantId || !oidcSettings?.loginHost) {
      throw new Error('ROPC registration is not configured. Missing ropcSignupPolicy, tenantId, or loginHost.');
    }

    const tokenEndpoint = `https://${oidcSettings.loginHost}/${oidcSettings.tenantId}/${oidcSettings.ropcSignupPolicy}/oauth2/v2.0/token`;

    const clientId = oidcSettings.apiClientId || oidcSettings.client_id;

    const body = new URLSearchParams();
    body.set('grant_type', 'password');
    body.set('client_id', clientId);
    body.set('username', registrationData.email);
    body.set('password', password);
    body.set('scope', `openid profile ${clientId} offline_access`);
    body.set('response_type', 'token id_token');

    body.set('extension_FirstName', registrationData.firstName);
    body.set('extension_LastName', registrationData.lastName);
    if (registrationData.phone) {
      body.set('extension_Phone', registrationData.phone);
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    try {
      const response = await firstValueFrom(
        this.http.post<RopcTokenResponse>(tokenEndpoint, body.toString(), { headers })
      );
      return response;
    } catch (error: unknown) {
      const httpError = error as { error?: RopcErrorResponse };
      if (httpError.error?.error_description) {
        const errorDesc = httpError.error.error_description;

        if (errorDesc.includes('AADB2C99002') || errorDesc.includes('already exists')) {
          throw new Error('An account with this email already exists. Please sign in instead.');
        }
        if (errorDesc.includes('AADB2C90117') || errorDesc.includes('password')) {
          throw new Error('Password does not meet requirements. Please use at least 8 characters with uppercase, lowercase, and numbers.');
        }
        if (errorDesc.includes('AADB2C90118') || errorDesc.includes('email')) {
          throw new Error('Please enter a valid email address.');
        }

        throw new Error(errorDesc);
      }

      throw new Error('Registration failed. Please try again.');
    }
  }

  extractUserInfo(idToken: string): RopcUserInfo {
    try {
      const payload = idToken.split('.')[1];
      const decoded = JSON.parse(atob(payload));

      return {
        userId: decoded.oid || decoded.sub || '',
        firstName: decoded.extension_FirstName || decoded.given_name,
        lastName: decoded.extension_LastName || decoded.family_name,
        email: decoded.emails?.[0] || decoded.email || decoded.signInNames?.emailAddress,
      };
    } catch (error) {
      this.loggingService.logException(error, 'Failed to parse ID token');
      return { userId: '' };
    }
  }
}
